import { HeroMesh } from "@/components/site/HeroMesh";
import { LocaleLink } from "@/components/site/LocaleLink";
import { lazy, Suspense } from "react";
import {
  Radio,
  BarChart3,
  AlertTriangle,
  ArrowRight,
  Landmark,
  ShoppingBag,
  Pill,
  Store,
  Car,
  Megaphone,
  Star,
  Building2,
  Network,
  Users,
  FileText,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { MetricsStrip } from "@/components/site/MetricsStrip";
import { ClientLogoStrip } from "@/components/site/ClientLogoStrip";
import { Reveal, RevealItem } from "@/components/site/Reveal";
import { REPORTS, REPORT_BASE } from "@/content/reports";
import { REPORT_DETAIL_SLUGS } from "@/content/report-details-slugs";
import { useT } from "@/lib/i18n/I18nProvider";
import { localizeTopic } from "@/content/topic-i18n";
import { useNewReports } from "@/hooks/use-new-reports";
import { NewReportBadge } from "@/components/site/NewReportBadge";
import amecLogo from "@/assets/associations/amec.webp";
import fibepLogo from "@/assets/associations/fibep.webp";
import bdvoLogo from "@/assets/associations/bdvo.webp";
import kidaLogo from "@/assets/associations/kida.webp";
import updataOneLogo from "@/assets/associations/updata-one.webp";

const MentionTicker = lazy(() =>
  import("@/components/site/MentionTicker").then((m) => ({ default: m.MentionTicker })),
);
import { HowItWorksSkeleton } from "@/components/site/HowItWorks";
import { PlansTeaserSkeleton } from "@/components/site/PlansTeaser";

import { pageSchemas } from "@/lib/seo/page-schema";
const HowItWorks = lazy(() =>
  import("@/components/site/HowItWorks").then((m) => ({ default: m.HowItWorks })),
);
const PlansTeaser = lazy(() =>
  import("@/components/site/PlansTeaser").then((m) => ({ default: m.PlansTeaser })),
);
const PlansTeaserBG = lazy(() =>
  import("@/components/site/PlansTeaserBG").then((m) => ({ default: m.PlansTeaserBG })),
);

const OG_IMAGE = "https://perceptica.com/og-image.jpg";


const services_en: Array<{
  icon: typeof Radio;
  title: string;
  body: string;
  items: string[];
  cta: string;
  to: "/media-monitoring" | "/media-analysis" | "/media-analysis/crisis-monitoring";
}> = [
  {
    icon: Radio,
    title: "Media Monitoring",
    body: "Track your brand's presence and reputation across multiple channels to gain valuable insights and make informed decisions by monitoring news, social media, blogs, and other relevant platforms.",
    items: ["Traditional Media Monitoring", "Social & Media Listening"],
    cta: "Learn more about media monitoring",
    to: "/media-monitoring",
  },
  {
    icon: BarChart3,
    title: "Media Analysis",
    body: "Make sense of the media noise with in-depth assessments of your brand's media coverage and evaluation of the impact of your PR efforts and identifying potential opportunities:",
    items: [
      "Employer Branding Analysis",
      "Reputation Analysis",
      "Crisis Monitoring & Analysis",
      "Competitive Intelligence",
      "Market Research",
    ],
    cta: "Explore analysis services",
    to: "/media-analysis",
  },
  {
    icon: AlertTriangle,
    title: "Crisis Monitoring",
    body: "React in minutes, not days. Real-time crisis detection and analyst-led briefings the moment your brand is at risk - so the response starts before the story does.",
    items: ["24/7 Real-time Alerts", "Analyst-led Crisis Briefings"],
    cta: "Explore crisis monitoring",
    to: "/media-analysis/crisis-monitoring",
  },
];

const industries_en: Array<{
  name: string;
  icon: typeof Landmark;
  to:
    | "/industries/fintech"
    | "/industries/fmcg"
    | "/industries/pharma"
    | "/industries/retail"
    | "/industries/automotive";
}> = [
  { name: "Fintech", icon: Landmark, to: "/industries/fintech" },
  { name: "FMCG", icon: ShoppingBag, to: "/industries/fmcg" },
  { name: "Pharma", icon: Pill, to: "/industries/pharma" },
  { name: "Retail", icon: Store, to: "/industries/retail" },
  { name: "Automotive", icon: Car, to: "/industries/automotive" },
];

const personas_en: Array<{
  role: string;
  blurb: string;
  icon: typeof Users;
  to:
    | "/who-we-help/pr-managers"
    | "/who-we-help/brand-managers"
    | "/who-we-help/pr-agency"
    | "/who-we-help/media-agencies"
    | "/who-we-help/employer-branding-experts";
}> = [
  { role: "PR Managers", blurb: "Prove the impact of every campaign with hard media data.", icon: Megaphone, to: "/who-we-help/pr-managers" },
  { role: "Brand Managers", blurb: "Track perception, share of voice and competitive narratives.", icon: Star, to: "/who-we-help/brand-managers" },
  { role: "PR Agencies", blurb: "Deliver client-ready intelligence and reporting at scale.", icon: Building2, to: "/who-we-help/pr-agency" },
  { role: "Media Agencies", blurb: "Sharpen planning and buying with real-time signals.", icon: Network, to: "/who-we-help/media-agencies" },
  { role: "Employer Branding", blurb: "Understand how candidates and employees perceive you.", icon: Users, to: "/who-we-help/employer-branding-experts" },
];

export function Index() {
  const { locale } = useT();
  const isBg = locale === "bg";
  const homepageReports = REPORTS.slice(0, 3);
  const newSlugs = useNewReports(homepageReports.map((r) => r.slug));

  const services: Array<{
    icon: typeof Radio;
    title: string;
    body: string;
    items: string[];
    cta: string;
    to: "/media-monitoring" | "/media-analysis" | "/media-analysis/crisis-monitoring";
  }> = isBg
    ? [
        {
          icon: Radio,
          title: "Медиен мониторинг",
          body:
            "Проследявайте присъствието и репутацията на вашата организация в множество канали, за да получите ценна информация и да вземате правилните решения - чрез наблюдение на новини, социални медии, блогове и други релевантни платформи.",
          items: ["Традиционни медии", "Онлайн медии", "Социални мрежи", "Блогове", "Форуми"],
          cta: "Научете повече за медийния мониторинг",
          to: "/media-monitoring",
        },
        {
          icon: BarChart3,
          title: "Медиен анализ",
          body:
            "Получете задълбочен анализ на медийното присъствие на вашата организация. Оценявайте въздействието на комуникационните си усилия и идентифицирайте възможности за по-добро позициониране.",
          items: [
            "Кризисен мониторинг и анализ",
            "Анализ на репутацията",
            "Анализ на конкуренцията",
            "Анализ на пазара",
            "Анализ на работодателска организация",
          ],
          cta: "Разгледайте аналитичните услуги",
          to: "/media-analysis",
        },
        {
          icon: AlertTriangle,
          title: "Кризисен мониторинг",
          body:
            "Реагирайте в рамките на минути, а не дни. Получавайте сигнали в реално време и експертни анализи още при първите признаци на репутационен риск.",
          items: ["Сигнали в реално време 24/7", "Кризисни анализи от експерти с дългогодишен опит"],
          cta: "Разгледайте кризисния мониторинг",
          to: "/media-analysis/crisis-monitoring",
        },
      ]
    : services_en;

  const industries: Array<{
    name: string;
    icon: typeof Landmark;
    to:
      | "/industries/fintech"
      | "/industries/fmcg"
      | "/industries/pharma"
      | "/industries/retail"
      | "/industries/automotive";
  }> = isBg
    ? [
        { name: "Финтех", icon: Landmark, to: "/industries/fintech" },
        { name: "FMCG", icon: ShoppingBag, to: "/industries/fmcg" },
        { name: "Фармация", icon: Pill, to: "/industries/pharma" },
        { name: "Търговия на дребно", icon: Store, to: "/industries/retail" },
        { name: "Автомобилна индустрия", icon: Car, to: "/industries/automotive" },
      ]
    : industries_en;

  const personas: Array<{
    role: string;
    blurb: string;
    icon: typeof Users;
    to:
      | "/who-we-help/pr-managers"
      | "/who-we-help/brand-managers"
      | "/who-we-help/pr-agency"
      | "/who-we-help/media-agencies"
      | "/who-we-help/employer-branding-experts";
  }> = isBg
    ? [
        { role: "PR мениджъри", blurb: "Измервайте ефекта от всяка кампания с надеждни медийни данни.", icon: Megaphone, to: "/who-we-help/pr-managers" },
        { role: "Бранд мениджъри", blurb: "Следете как се възприема вашата организация, какво е нейното медийно присъствие и как се позиционира спрямо конкурентите.", icon: Star, to: "/who-we-help/brand-managers" },
        { role: "PR агенции", blurb: "Предоставяйте на клиентите си ясни данни и професионални анализи.", icon: Building2, to: "/who-we-help/pr-agency" },
        { role: "Медийни агенции", blurb: "Използвайте данни в реално време за по-ефективно медийно планиране.", icon: Network, to: "/who-we-help/media-agencies" },
        { role: "Работодателска организация", blurb: "Анализирайте как кандидати и служители възприемат вашата компания като работодател.", icon: Users, to: "/who-we-help/employer-branding-experts" },
      ]
    : personas_en;

  const tx = {
    requestDemo: isBg ? "Заявете демо" : "Request a demo",
    seePlatform: isBg ? "Вижте платформата" : "See the platform",
    trustedBy: isBg ? "Perceptica партнира със 70+ бранда в Европа" : "Trusted by 70+ brands across Europe",
    listenSignals: isBg ? "Разчитайте сигналите в " : "Listen to the signals in ",
    yourEnv: isBg ? "информационната среда" : "your environment",
    helpBrands: isBg
      ? "Помагаме на организациите да наблюдават, анализират и разбират медийните разговори в реално време."
      : "We help brands monitor, understand, and respond to media conversations in real time.",
    industries: isBg ? "Индустрии" : "Industries",
    builtForSectors: isBg ? "Медиен мониторинг и анализ, адаптиран към вашата индустрия" : "Built for the sectors that move markets",
    exploreIndustries: isBg ? "Разгледайте всички индустрии" : "Explore all industries",
    whoWeHelp: isBg ? "ПО РОЛИ" : "Who we help",
    builtForRole: isBg ? "Решения за всяка роля в комуникациите" : "Built for your role",
    seeAllRoles: isBg ? "Вижте всички роли" : "See all roles",
    reportsLabel: isBg ? "Доклади" : "Reports",
    freeReports: isBg
      ? "Безплатни доклади от анализаторите на Perceptica"
      : "Free reports from the Perceptica research desk",
    reportsBlurb: isBg
      ? "Задълбочени анализи и секторни доклади, достъпни за свободно изтегляне."
      : "Media analyses and sector deep-dives open to download for everyone.",
    browseReports: isBg ? "Вижте всички доклади" : "Browse all reports",
    memberOf: isBg ? "Perceptica е част от" : "Member of",
    reportFallback: isBg ? "Репортаж" : "Report",
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader transparentOverHero />
      <main id="main">

      {/* HERO - DARK */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        {/* Atmospheric layers */}
        <div className="grid-overlay" />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full opacity-50 blur-[120px]"
          style={{ background: "radial-gradient(circle, #6B5BF5 0%, transparent 70%)" }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-32 h-[520px] w-[520px] rounded-full opacity-40 blur-[120px]"
          style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
        />
        <HeroMesh
          variant="dark"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-25"
        />
        <div className="noise-overlay" />

        <div className="relative mx-auto max-w-[1200px] px-6 pt-28 pb-32 text-center lg:pt-36 lg:pb-44">
          <h1 className="font-display mx-auto max-w-[18ch] text-[clamp(2.5rem,5.4vw,4.5rem)] font-medium leading-[1.05] text-white">
            <span className="text-gradient-brand">{isBg ? "Медиен мониторинг и анализ" : "Media monitoring and analysis"}</span>
            <br />
            {isBg ? "в Югоизточна Европа" : "for Southeast Europe"}
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-white/70 lg:text-[1.0625rem]">
            {isBg
              ? "Следете релевантното за вас съдържание в над 150 000 източника, включително традиционни и онлайн медии, социални мрежи, блогове и форуми."
              : "Perceptica helps teams monitor, understand, and act on every media signal with clarity and precision."}
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="xl">
              <LocaleLink to="/request-a-demo">{tx.requestDemo}</LocaleLink>
            </Button>
            <Button asChild variant="outlineLight" size="xl">
              <LocaleLink to="/media-monitoring">{tx.seePlatform}</LocaleLink>
            </Button>
          </div>

          {/* Live product preview */}
          <div className="mt-16">
            <Suspense
              fallback={
                <div
                  aria-hidden
                  className="mx-auto w-full max-w-3xl rounded-2xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-md sm:p-6"
                  style={{ minHeight: 224 }}
                >
                  <div className="mb-4 flex items-center justify-between">
                    <div className="h-3 w-28 animate-pulse rounded bg-white/10" />
                    <div className="hidden h-3 w-24 animate-pulse rounded bg-white/10 sm:block" />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-[1.6fr_1fr]">
                    <ul className="space-y-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <li
                          key={i}
                          className="h-10 animate-pulse rounded-lg bg-white/[0.05] ring-1 ring-inset ring-white/[0.04]"
                        />
                      ))}
                    </ul>
                    <div className="h-[152px] animate-pulse rounded-lg bg-white/[0.04] ring-1 ring-inset ring-white/[0.04]" />
                  </div>
                </div>
              }
            >
              <MentionTicker />
            </Suspense>
          </div>

          {/* Trust strip */}
          <div className="mt-16 flex justify-center">
            <p className="text-[13px] text-white/80">{tx.trustedBy}</p>
          </div>

          {/* Animated horizon line */}
          <div className="absolute inset-x-0 bottom-0 flex justify-center">
            <span className="pulse-line h-px w-2/3 bg-gradient-brand" />
          </div>
        </div>
      </section>

      {/* METRICS */}
      <MetricsStrip />

      {/* CLIENT LOGOS */}
      <ClientLogoStrip />

      {/* SERVICES - LIGHT */}
      <section className="relative overflow-hidden bg-background text-foreground">
        <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-[clamp(2rem,3.6vw,3rem)] leading-[1.08] text-foreground">
              {tx.listenSignals}<span className="text-gradient-brand">{tx.yourEnv}</span>
            </h2>
            <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
              {tx.helpBrands}
            </p>
          </Reveal>

          <Reveal stagger className="mt-16 grid gap-6 lg:grid-cols-3">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <RevealItem key={s.title} className="h-full">
                <article className="hover-lift group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-background p-8 shadow-[var(--shadow-soft)]">
                  <span className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-secondary/40 to-transparent" />
                  <span className="pointer-events-none absolute -right-24 -bottom-24 h-56 w-56 rounded-full bg-gradient-brand opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-25" />

                  <span className="relative inline-flex h-10 w-10 items-center justify-center rounded-[8px] bg-gradient-brand text-white shadow-[0_8px_24px_-8px_rgba(255,45,120,0.55)]">
                    <Icon className="h-5 w-5" />
                  </span>

                  <h3 className="font-display relative mt-5 text-xl tracking-[-0.02em] text-foreground">
                    {s.title}
                  </h3>
                  <p className="relative mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
                    {s.body}
                  </p>
                  {s.items.length > 0 && (
                    <ul className="relative mt-6 space-y-2.5 text-[13.5px] text-foreground/85">
                      {s.items.map((i) => (
                        <li key={i} className="flex items-start gap-2.5">
                          <span className="mt-2 block h-1.5 w-1.5 flex-none rounded-full bg-accent" />
                          {i}
                        </li>
                      ))}
                    </ul>
                  )}
                  <LocaleLink
                    to={s.to}
                    className="group/cta relative mt-auto inline-flex items-center gap-1.5 pt-8 text-[13px] font-semibold tracking-[-0.005em] text-accent-strong transition-colors hover:text-foreground"
                  >
                    {s.cta}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/cta:translate-x-1" />
                  </LocaleLink>
                </article>
                </RevealItem>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES - DARK */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground py-24 lg:py-32">
        <div
          aria-hidden
          className="pointer-events-none absolute top-0 right-0 h-[420px] w-[420px] rounded-full opacity-30 blur-[120px]"
          style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
        />
        <div className="noise-overlay" />
        <div className="relative mx-auto max-w-[1200px] px-6 lg:px-10">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">{tx.industries}</p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-white">
                {tx.builtForSectors}
              </h2>
            </div>
            <LocaleLink
              to="/industries"
              className="group inline-flex items-center gap-2 text-[13px] font-semibold tracking-[-0.005em] text-white/80 transition-colors hover:text-white"
            >
              {tx.exploreIndustries}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </LocaleLink>
          </Reveal>
          <Reveal stagger className="mt-12 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            {industries.map((i) => {
              const Icon = i.icon;
              return (
                <RevealItem key={i.name}>
                  <LocaleLink
                    to={i.to}
                    className="hover-lift gradient-border group flex h-full flex-col items-start gap-4 rounded-2xl bg-white/[0.03] p-6 backdrop-blur-sm"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-[8px] bg-white/10 text-white transition-colors group-hover:bg-gradient-brand">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="font-display text-[16px] tracking-[-0.01em] text-white">
                      {i.name}
                    </span>
                    <span className="mt-auto inline-flex items-center gap-1 text-[12px] font-semibold tracking-[-0.005em] text-accent opacity-0 transition-opacity group-hover:opacity-100">
                      {isBg ? `Научете повече за ${i.name}` : `Learn more about ${i.name}`}
                      <ArrowRight className="h-3 w-3" />
                    </span>
                  </LocaleLink>
                </RevealItem>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* PERSONAS - LIGHT */}
      <section className="relative overflow-hidden bg-background text-foreground">
        <div className="relative mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-strong">{tx.whoWeHelp}</p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                {tx.builtForRole}
              </h2>
            </div>
            <LocaleLink
              to="/who-we-help"
              className="group inline-flex items-center gap-2 text-[13px] font-semibold tracking-[-0.005em] text-foreground/70 transition-colors hover:text-foreground"
            >
              {tx.seeAllRoles}
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </LocaleLink>
          </Reveal>
          <Reveal stagger className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-5">
            {personas.map((p) => {
              const Icon = p.icon;
              return (
                <RevealItem key={p.role}>
                  <LocaleLink
                    to={p.to}
                    className="hover-lift group flex h-full flex-col rounded-2xl border border-border/60 bg-background p-6 shadow-[var(--shadow-soft)] transition-colors hover:border-accent/40"
                  >
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-[8px] bg-gradient-brand text-white shadow-[0_8px_24px_-8px_rgba(255,45,120,0.55)]">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-display mt-5 text-[17px] tracking-[-0.015em] text-foreground">
                      {p.role}
                    </h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{p.blurb}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-6 text-[12px] font-semibold tracking-[-0.005em] text-accent-strong transition-colors group-hover:text-foreground">
                      {isBg ? `Научете повече за ${p.role}` : `Learn more about ${p.role}`}
                      <ArrowRight className="h-3 w-3" />
                    </span>
                  </LocaleLink>
                </RevealItem>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <Suspense fallback={<HowItWorksSkeleton />}>
        <HowItWorks />
      </Suspense>

      {/* REPORTS HIGHLIGHT - LIGHT */}
      <section className="relative overflow-hidden bg-background text-foreground py-24 lg:py-28">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <Reveal>
            <article className="relative grid gap-10 rounded-3xl border border-border/60 bg-secondary/40 p-8 shadow-[var(--shadow-soft)] lg:grid-cols-[1.1fr_1fr] lg:gap-16 lg:p-12">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-strong">{tx.reportsLabel}</p>
                <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                  {tx.freeReports}
                </h2>
                <p className="mt-4 max-w-md text-[14.5px] leading-relaxed text-muted-foreground">
                  {tx.reportsBlurb}
                </p>
                <LocaleLink
                  to="/reports"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-[13px] font-medium text-primary-foreground transition-opacity hover:opacity-90"
                >
                  {tx.browseReports}
                  <ArrowRight className="h-4 w-4" />
                </LocaleLink>
              </div>
              <ul className="flex flex-col gap-3">
                {homepageReports.map((r) => {
                  const hasDetails = REPORT_DETAIL_SLUGS.has(r.slug);
                  const cardClass =
                    "group flex items-start gap-3 rounded-xl border border-border/60 bg-background p-4 transition-all hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-[var(--shadow-soft)]";
                  const inner = (
                    <>
                      <span className="mt-0.5 inline-flex h-8 w-8 flex-none items-center justify-center rounded-[6px] bg-secondary text-muted-foreground transition-colors group-hover:bg-accent/20 group-hover:text-accent">
                        <FileText className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[12px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                            {r.topic ? localizeTopic(r.topic, locale) ?? r.topic : tx.reportFallback}{r.year ? ` · ${r.year}` : ""}
                          </p>
                          {newSlugs.has(r.slug) && <NewReportBadge />}
                        </div>
                        <p className="mt-1 text-[14px] leading-snug text-foreground">{isBg && r.titleBg ? r.titleBg : r.title}</p>
                      </div>
                    </>
                  );
                  return (
                    <li key={r.slug}>
                      {hasDetails ? (
                        <LocaleLink to="/reports/$slug" params={{ slug: r.slug }} className={cardClass}>
                          {inner}
                        </LocaleLink>
                      ) : (
                        <a
                          href={`${REPORT_BASE}/${r.slug}/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={cardClass}
                        >
                          {inner}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </article>
          </Reveal>
        </div>
      </section>

      {/* PLANS */}
      <Suspense fallback={<PlansTeaserSkeleton />}>
        {isBg ? <PlansTeaserBG /> : <PlansTeaser />}
      </Suspense>

      {/* ASSOCIATIONS - quiet typographic strip */}
      <section className="border-t border-border/60 bg-background py-16">
        <div className="mx-auto flex max-w-[1200px] flex-col items-start gap-6 px-6 lg:flex-row lg:items-center lg:justify-between lg:px-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            {tx.memberOf}
          </p>
          <ul className="flex flex-wrap items-center gap-x-10 gap-y-6">
            {[
              { name: "AMEC", src: amecLogo },
              { name: "FIBEP", src: fibepLogo },
              { name: "BDVO", src: bdvoLogo },
              { name: "KIDA", src: kidaLogo },
              { name: "UPDATA ONE", src: updataOneLogo },
            ].map((p) => (
              <li key={p.name} className="flex items-center">
                <img
                  src={p.src}
                  alt={`${p.name} member logo`}
                  width={120}
                  height={40}
                  loading="lazy"
                  className="h-10 w-auto object-contain opacity-70 grayscale transition duration-300 hover:opacity-100 hover:grayscale-0"
                />
              </li>
            ))}
          </ul>
        </div>
      </section>

      </main>
      <SiteFooter />
    </div>
  );
}
